import { useEffect } from 'react';
import useApi from './useApi';
import { EventDataForVoyages, useEventStore } from '../context/EventContext';
import TankersEventFactory from '../factory/TankersEventFactory';
import { TankersEventDataBM } from '../types/BusinessModel/TankersEventDataBM';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import { useVesselStore } from '../context/VesselContext';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from './endpoints/queryParamsHelper';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { getAllReportEventsItems } from './report-events/offlineReportEventsApi';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { processApiEndpointError } = apiEndpointUtils();
    const { state, getApiData } = useApi();
    const { selectedVessel } = useVesselStore();
    const { setDeltaEventArray, eventDataForVoyageList, setEventDataForVoyageList } = useEventStore();
    const { newEventList } = TankersEventFactory();

    const requestVoyageEvents = async (voyageNumber: string, background?: boolean): Promise<TankersEventDataBM | undefined> => {
        const result = (await getApiData({
            endpoint: apiEndpoints['getTankersEventDataDeltaForVoyage'].url,
            queryParam: buildQueryParams({ voyageNumber1: voyageNumber, voyageNumber2: '', imoNumber: selectedVessel?.imoNumber }),
            callInBackground: background,
        })) as TankersEventDataBM | undefined;
        return result;
    };

    const getEventListForVoyage = async (voyageNumber: number) => {
        if (!selectedVessel || !voyageNumber) return;

        let voyList = [voyageNumber.toString()];
        if (eventDataForVoyageList?.imoNumber && eventDataForVoyageList.imoNumber === selectedVessel.imoNumber) {
            voyList = voyList.concat(eventDataForVoyageList.voyageNumbers);
        }

        const voySet = new Set(voyList);
        voyList = Array.from(voySet.values());

        setEventDataForVoyageList({ imoNumber: selectedVessel.imoNumber, voyageNumbers: voyList } as EventDataForVoyages);

        const offlineReportEvents = await getAllReportEventsItems();
        const result = await requestVoyageEvents(voyageNumber.toString());

        if (result) {
            const evList = newEventList(result, offlineReportEvents, false);
            setDeltaEventArray(evList || []);
        } else {
            setDeltaEventArray([]);
        }
    };

    const getEventsForVoyageList = async (voyageList: string[], callInBackground?: boolean) => {
        if (!selectedVessel || !voyageList || voyageList.length < 1) return;

        const requestedVoyages = Array.from(new Set(voyageList.filter((voy) => !!voy))).sort();
        if (requestedVoyages.length === 0) return;

        let voyList = [...requestedVoyages];
        if (eventDataForVoyageList?.imoNumber && eventDataForVoyageList.imoNumber === selectedVessel.imoNumber) {
            voyList = voyList.concat(eventDataForVoyageList.voyageNumbers);
        }

        const voySet = new Set(voyList);
        const uniqueVoyages = Array.from(voySet.values());
        setEventDataForVoyageList({ imoNumber: selectedVessel.imoNumber, voyageNumbers: uniqueVoyages } as EventDataForVoyages);

        const offlineReportEvents = await getAllReportEventsItems();

        // Execute all voyage requests in parallel and collect results
        const results = await Promise.all(
            requestedVoyages.map((voyageNumber, index) => requestVoyageEvents(voyageNumber, index === 0 ? callInBackground : true))
        );

        // Merge all events from all voyage responses into a single array, deduplicating by eventId
        const eventMap = new Map<string, TankersEvent>();
        results.forEach((result) => {
            if (result) {
                const evList = newEventList(result, offlineReportEvents, false);
                if (evList && evList.length > 0) {
                    evList.forEach((event) => {
                        if (event.eventId && !eventMap.has(event.eventId)) {
                            eventMap.set(event.eventId, event);
                        }
                    });
                }
            }
        });

        // Set all unique events at once after all requests complete
        const mergedEvents = Array.from(eventMap.values());
        setDeltaEventArray(mergedEvents.length > 0 ? mergedEvents : []);
    };

    useEffect(() => {
        if (state.status === 'error') {
            processApiEndpointError(state);
        }
    }, [state]);

    return { state, getEventListForVoyage, getEventsForVoyageList };
};
